import React, { useState, useEffect } from 'react';
import { Input, FormControl, FormLabel, Select, Option } from '@mui/joy';

interface TimePicker15MinProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  placeholder?: string;
  error?: boolean;
  required?: boolean;
}

export const TimePicker15Min: React.FC<TimePicker15MinProps> = ({
  value,
  onChange,
  label,
  disabled = false,
  size = 'md',
  placeholder = 'Select time',
  error = false,
  required = false
}) => {
  // Working hours range for dropdown options (inclusive)
  const MIN_HOUR = 7;  // 07:00
  const MAX_HOUR = 19; // 19:59 (up to 19:45 in 15-min steps)

  const [displayValue, setDisplayValue] = useState(value);
  const [isCustomTime, setIsCustomTime] = useState(false);

  // Generate 15-minute interval options
  const timeOptions = React.useMemo(() => {
    const options = [];
    for (let hour = MIN_HOUR; hour <= MAX_HOUR; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        options.push({
          value: timeString,
          label: timeString
        });
      }
    }
    return options;
  }, []);

  // Check if the current value is a 15-minute interval
  const is15MinInterval = (timeStr: string) => {
    if (!timeStr) return true;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return minutes % 15 === 0;
  };

  // Handle value changes
  useEffect(() => {
    if (value) {
      const inOptions = timeOptions.some((option) => option.value === value);
      setDisplayValue(value);
      // Use dropdown only for 15-min values that are within our working-hour options.
      // Anything else (including legacy/out-of-range values) falls back to the native time input.
      setIsCustomTime(!is15MinInterval(value) || !inOptions);
    } else {
      setDisplayValue('');
      setIsCustomTime(false);
    }
  }, [value, timeOptions]);

  const handleTimeChange = (newValue: string | null) => {
    if (newValue) {
      setDisplayValue(newValue);
      setIsCustomTime(false);
      onChange(newValue);
    }
  };

  const handleCustomTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setDisplayValue(newValue);
    setIsCustomTime(true);
    onChange(newValue);
  };

  // If it's a custom time (not 15-minute interval), show the original time input
  if (isCustomTime) {
    return (
      <FormControl size={size} error={error} required={required}>
        {label && <FormLabel>{label}</FormLabel>}
        <Input
          type="time"
          value={displayValue}
          onChange={handleCustomTimeChange}
          disabled={disabled}
          placeholder={placeholder}
        />
      </FormControl>
    );
  }

  // For 15-minute intervals, use the dropdown
  return (
    <FormControl size={size} error={error} required={required}>
      {label && <FormLabel>{label}</FormLabel>}
      <Select
        value={displayValue || ''}
        onChange={(_, newValue) => handleTimeChange(newValue)}
        disabled={disabled}
        placeholder={placeholder}
      >
        {timeOptions.map((option) => (
          <Option key={option.value} value={option.value}>
            {option.label}
          </Option>
        ))}
      </Select>
    </FormControl>
  );
}; 